import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { FlaskConicalIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { displayName, useAuth } from '@/hooks/useAuth';
import AskPanel from './-components/AskPanel';
import GeometryImportButton from './-components/GeometryImportButton';
import { useGeometryImport } from './-components/useGeometryImport';
import YourWorkCard from './-components/YourWorkCard';
import RecentsCard from './-components/RecentsCard';

function getGreetingKey() {
  const hour = new Date().getHours();
  if (hour < 12) return 'home.greetingMorning' as const;
  if (hour < 18) return 'home.greetingAfternoon' as const;
  return 'home.greetingEvening' as const;
}

/**
 * The home page (ENG-2028).
 *
 * Three regions, in the order someone actually needs them: a question box,
 * because most visits start with one; what has stopped and is waiting on the
 * reader; and what they last had open.
 *
 * What is deliberately NOT here: cluster utilisation, queue finish times, a
 * recent-threads strip, and a weekly "the AI saved you N hours" line. All four
 * were in the design and all four would have to be invented — no endpoint
 * answers them — and a made-up number on the first screen of the app is worse
 * than no number.
 *
 * The three header actions are one per way in: import a part to look at
 * (Diagnosis), import a part to mesh (MeshGen), or start a study. They share
 * one upload and differ only in where it lands — see `useGeometryImport`.
 *
 * The page owns one import per destination, one per header button. The Ask box
 * no longer shares them: a part given to that box becomes a chat attachment
 * rather than an import (ENG-2074), so it takes the assistant's own upload path
 * and the reader stays on this page.
 */
function HomePage() {
  const { t } = useTranslation();
  // `profile`, not `user`: it is populated on first paint, so a cold boot
  // greets you by name instead of falling back until `/auth/current` lands.
  const { profile } = useAuth();
  const greetingName = displayName(profile) ?? t('home.fallbackName');
  const repairImport = useGeometryImport('diagnosis');
  const meshImport = useGeometryImport('meshgen');

  return (
    <div className="h-full overflow-auto bg-muted p-6 md:p-8">
      {repairImport.elements}
      {meshImport.elements}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <div className="flex items-end gap-3">
          <div>
            <p className="text-sm text-muted-foreground">
              {t(getGreetingKey())}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-foreground">
              {greetingName}
            </h1>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <GeometryImportButton
              destination="diagnosis"
              onClick={repairImport.browse}
              isUploading={repairImport.isUploading}
              uploadPercent={repairImport.uploadPercent}
            />
            <GeometryImportButton
              destination="meshgen"
              onClick={meshImport.browse}
              isUploading={meshImport.isUploading}
              uploadPercent={meshImport.uploadPercent}
            />
            <Button asChild size="sm">
              <Link to="/simops/new">
                <FlaskConicalIcon aria-hidden="true" />
                {t('home.newStudy')}
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <AskPanel />
          </div>
          <YourWorkCard />
        </div>

        <RecentsCard />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});
